import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Drawer, IconButton, Stack,
  TextField, Typography,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import useFetch from '../services/useFetch';
import PostDataRetData from '../services/PostDataRetData';
import PageHeader from '../components/layout/PageHeader';
import SnackbarEx from '../components/Helpers/SnackbarEx';
import { currencyFormatter } from '../components/Helpers/CurrencyFormat';
import { dataGridSx, quickFilterSx } from '../components/ui/dataGridStyles';
import { withResizableColumns, useColumnWidths } from '../components/ui/ResizableGridHeader';
import { tokens } from '../theme';

const baseUrl = process.env.REACT_APP_NETLIFY_API_URL || process.env.REACT_APP_API_URL;

const DEFAULT_WIDTHS = {
  batchId: 150,
  bornOn: 110,
  bottledDate: 120,
  estimatedReadyDate: 120,
  beanName: 140,
  alcoholName: 140,
  beansOz: 90,
  alcoholOz: 100,
  productionJarSizeOz: 90,
  remainingOz: 110,
  totalBatchCost: 120,
  costPerOz: 100,
  notes: 160,
};

function blankInput() {
  return { ingredientId: null, name: '', amount: '', measurement: '' };
}

function toDateInputValue(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function addYearsIso(dateStr, years = 1) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setFullYear(d.getFullYear() + years);
  return toDateInputValue(d);
}

function emptyForm() {
  return {
    batchId: '',
    bornOn: toDateInputValue(new Date()),
    yieldOz: '',
    remainingOz: '',
    beanName: '',
    alcoholName: '',
    beansOz: '',
    alcoholOz: '',
    productionJarSizeOz: '',
    productionJarsUsed: '',
    largeLabelsUsed: '',
    laborHours: '',
    estimatedReadyDate: addYearsIso(toDateInputValue(new Date()), 1),
    bottledDate: '',
    notes: '',
    inputs: [blankInput()],
  };
}

function formFromBatch(batch) {
  if (!batch) return emptyForm();
  return {
    batchId: batch.batchId || '',
    bornOn: toDateInputValue(batch.bornOn),
    yieldOz: batch.yieldOz ?? '',
    remainingOz: batch.remainingOz ?? '',
    beanName: batch.beanName || '',
    alcoholName: batch.alcoholName || '',
    beansOz: batch.beansOz ?? '',
    alcoholOz: batch.alcoholOz ?? '',
    productionJarSizeOz: batch.productionJarSizeOz ?? '',
    productionJarsUsed: batch.productionJarsUsed ?? '',
    largeLabelsUsed: batch.largeLabelsUsed ?? '',
    laborHours: batch.laborHours ?? '',
    estimatedReadyDate: toDateInputValue(batch.estimatedReadyDate),
    bottledDate: toDateInputValue(batch.bottledDate),
    notes: batch.notes || '',
    inputs: Array.isArray(batch.inputs) && batch.inputs.length
      ? batch.inputs.map((r) => ({
        ingredientId: r.ingredientId ?? null,
        name: r.name || '',
        amount: r.amount ?? '',
        measurement: r.measurement || '',
      }))
      : [blankInput()],
  };
}

function numOrUndef(value) {
  if (value === '' || value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function payloadFromForm(form) {
  return {
    batchId: form.batchId.trim(),
    yieldOz: Number(form.yieldOz),
    remainingOz: numOrUndef(form.remainingOz),
    bornOn: form.bornOn || null,
    beanName: form.beanName.trim() || undefined,
    alcoholName: form.alcoholName.trim() || undefined,
    beansOz: numOrUndef(form.beansOz),
    alcoholOz: numOrUndef(form.alcoholOz),
    productionJarSizeOz: numOrUndef(form.productionJarSizeOz),
    productionJarsUsed: numOrUndef(form.productionJarsUsed),
    largeLabelsUsed: numOrUndef(form.largeLabelsUsed),
    laborHours: numOrUndef(form.laborHours),
    estimatedReadyDate: form.estimatedReadyDate || null,
    bottledDate: form.bottledDate || null,
    notes: form.notes.trim() || undefined,
    inputs: (form.inputs || [])
      .filter((r) => r.ingredientId != null && Number(r.amount) > 0)
      .map((r) => ({
        ingredientId: r.ingredientId,
        name: r.name,
        amount: Number(r.amount),
        measurement: r.measurement,
      })),
  };
}

function sameUom(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

/** Preview of inventory unit cost × amount (server is the source of truth on save). */
function estimateLineCost(ing, amount, measurement) {
  const amt = Number(amount) || 0;
  if (!ing || amt <= 0) return 0;
  const recipeUnit = Number(ing.pricePerUnit) || 0;
  const parUnit = Number(ing.parPricePerUnit) || 0;

  if (ing.reusable) {
    const fromUsed = Math.min(Number(ing.usedStock) || 0, amt);
    const fromNew = Math.max(amt - fromUsed, 0);
    return fromNew * (parUnit || recipeUnit);
  }
  const parIsDistinct = parUnit && !sameUom(ing.parUoM, ing.recipeUoM);
  if (parIsDistinct && sameUom(measurement, ing.parUoM)) return amt * parUnit;
  if (recipeUnit) return amt * recipeUnit;
  return amt * parUnit;
}

function matchOption(options, name) {
  const n = String(name || '')
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!n) return null;
  return options.find((o) => {
    const label = String(o.label || '')
      .toLowerCase()
      .replace(/[()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const base = label.split('(')[0].trim();
    return label === n || base === n || label.includes(n) || n.includes(label) || n.includes(base);
  }) || null;
}

function isBeanIngredient(ing) {
  const cat = String(ing.category || '').toLowerCase();
  return cat === 'beans' || cat === 'spices' || cat.includes('spice');
}

function isAlcoholIngredient(ing) {
  const cat = String(ing.category || '').toLowerCase();
  return cat === 'alcohol' || cat === 'extract';
}

function derivedInputsFromProduction(form, options) {
  const lines = [];
  const seen = new Set();
  const add = (ing, amount, measurement) => {
    if (!ing || !(Number(amount) > 0) || seen.has(String(ing.id))) return;
    seen.add(String(ing.id));
    lines.push({
      ingredientId: ing.id,
      name: ing.label,
      amount,
      measurement: measurement || ing.measurement || '',
    });
  };

  add(matchOption(options, form.beanName), form.beansOz, 'oz');
  add(matchOption(options, form.alcoholName), form.alcoholOz, 'oz');

  const jar = matchOption(options, form.productionJarSizeOz ? `prod jar ${form.productionJarSizeOz}` : 'production jar')
    || matchOption(options, 'mason')
    || options.find((o) => /mason|prod jar|production jar/i.test(o.label));
  add(jar, form.productionJarsUsed, jar?.parUoM || jar?.measurement || 'each');

  const label = options.find((o) => /large label/i.test(o.label))
    || options.find((o) => /label/i.test(o.label));
  add(label, form.largeLabelsUsed, label?.parUoM || label?.measurement || 'each');

  return lines;
}

/** Keep manual raw-input rows; refresh bean/alcohol/jar/label lines from production details. */
function syncInputsFromProduction(prevForm, patch, options) {
  const next = { ...prevForm, ...patch };
  const derived = derivedInputsFromProduction(next, options);
  const derivedIds = new Set(derived.map((d) => String(d.ingredientId)));
  const extras = (prevForm.inputs || []).filter((row) => {
    if (row.ingredientId == null) return Boolean(row.name || row.amount);
    return !derivedIds.has(String(row.ingredientId));
  });
  next.inputs = (derived.length || extras.length) ? [...derived, ...extras] : [blankInput()];
  return next;
}

function Field({ label, ...props }) {
  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      InputLabelProps={props.type === 'date' ? { shrink: true } : undefined}
      {...props}
    />
  );
}

function IngredientPick({ label, options, valueName, onPick }) {
  const selected = useMemo(() => {
    if (!valueName) return null;
    return options.find((o) => o.label === valueName)
      || matchOption(options, valueName)
      || null;
  }, [options, valueName]);

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_, val) => onPick(val)}
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
      size="small"
      fullWidth
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" placeholder="Search inventory" />
      )}
    />
  );
}

function BatchDrawer({ open, batch, onClose, onSaved }) {
  const isEdit = Boolean(batch?.batchId);
  const { data: ingredients = [] } = useFetch('api/ingredients', false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const options = useMemo(
    () => (ingredients || [])
      .filter((ing) => String(ing.category || '').toLowerCase() !== 'batch')
      .map((ing) => ({
        id: ing.ingredientId,
        label: ing.ingredient,
        category: ing.category || '',
        measurement: ing.recipeUoM || ing.parUoM || '',
        recipeUoM: ing.recipeUoM || '',
        parUoM: ing.parUoM || '',
        pricePerUnit: Number(ing.pricePerUnit) || 0,
        parPricePerUnit: Number(ing.parPricePerUnit) || 0,
        reusable: Boolean(ing.reusable),
        usedStock: Number(ing.usedStock) || 0,
      })),
    [ingredients],
  );

  const beanOptions = useMemo(() => options.filter(isBeanIngredient), [options]);
  const alcoholOptions = useMemo(() => options.filter(isAlcoholIngredient), [options]);

  useEffect(() => {
    if (!open) return;
    setError('');
    const base = formFromBatch(batch);
    const hasExplicit = (base.inputs || []).some((row) => row.ingredientId != null && Number(row.amount) > 0);
    if (!hasExplicit && options.length) {
      const derived = derivedInputsFromProduction(base, options);
      if (derived.length) base.inputs = derived;
    }
    setForm(base);
  }, [open, batch, options]);

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'bornOn' && !isEdit) {
        next.estimatedReadyDate = addYearsIso(value, 1);
      }
      return next;
    });
  };

  const setProductionField = (key, value) => {
    setForm((prev) => {
      let patch = { [key]: value };
      if (key === 'bornOn' && !isEdit) {
        patch = { ...patch, estimatedReadyDate: addYearsIso(value, 1) };
      }
      const productionKeys = new Set([
        'beanName', 'alcoholName', 'beansOz', 'alcoholOz',
        'productionJarSizeOz', 'productionJarsUsed', 'largeLabelsUsed',
      ]);
      if (productionKeys.has(key)) {
        return syncInputsFromProduction(prev, patch, options);
      }
      return { ...prev, ...patch };
    });
  };

  const pickBean = (ing) => {
    setForm((prev) => syncInputsFromProduction(prev, {
      beanName: ing?.label || '',
    }, options));
  };

  const pickAlcohol = (ing) => {
    setForm((prev) => syncInputsFromProduction(prev, {
      alcoholName: ing?.label || '',
    }, options));
  };

  const cogsPreview = useMemo(() => {
    const lines = (form.inputs || []).map((row) => {
      const ing = options.find((o) => String(o.id) === String(row.ingredientId));
      const lineCost = estimateLineCost(ing, row.amount, row.measurement);
      return { ...row, lineCost, unitCost: ing?.pricePerUnit ?? 0 };
    });
    const total = lines.reduce((sum, line) => sum + (Number(line.lineCost) || 0), 0);
    const yieldOz = Number(form.yieldOz);
    const costPerOz = yieldOz > 0 ? total / yieldOz : null;
    return { lines, total, costPerOz };
  }, [form.inputs, form.yieldOz, options]);

  const canSubmit = Boolean(form.batchId.trim())
    && Number(form.yieldOz) > 0
    && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError('');
    const payload = payloadFromForm(form);

    let data;
    let err;
    if (isEdit) {
      const tkn = sessionStorage.getItem('urtkn');
      const location = localStorage.getItem('usrlctn');
      try {
        const response = await axios.put(
          `${baseUrl}/api/batches/${encodeURIComponent(batch.batchId)}`,
          { data: payload, location },
          { headers: { Authorization: `Bearer ${tkn}` } },
        );
        data = response.data;
      } catch (caught) {
        err = caught;
      }
    } else {
      const result = await PostDataRetData(payload, 'api/batches');
      data = result.data;
      err = result.error;
    }
    setSubmitting(false);

    if (err || data?.error) {
      setError(
        data?.error?.message
        || err?.response?.data?.error?.message
        || `Could not ${isEdit ? 'save' : 'create'} batch`,
      );
      return;
    }
    onSaved?.(data?.data, isEdit);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          backgroundColor: tokens.surface,
          borderLeft: `1px solid ${tokens.border}`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.25, pb: 2, borderBottom: `1px solid ${tokens.border}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.accent, fontWeight: 700, mb: 0.75 }}>
              {isEdit ? 'Edit batch' : 'New batch'}
            </Typography>
            <Typography sx={{ fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650, fontSize: 22, color: tokens.ink }}>
              {isEdit ? batch.batchId : 'Production batch'}
            </Typography>
            <Typography sx={{ mt: 0.5, color: tokens.inkMuted, fontSize: 13 }}>
              Raw usage is deducted from inventory. Batch COGS is calculated from each input’s unit cost.
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, py: 2, flex: 1, overflowY: 'auto' }}>
        <Stack spacing={1.5}>
          <Field
            label="Batch ID"
            value={form.batchId}
            onChange={(e) => setField('batchId', e.target.value)}
            placeholder="e.g. VE260204-01"
            required
            disabled={isEdit}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <Field label="Born on" type="date" value={form.bornOn} onChange={(e) => setField('bornOn', e.target.value)} />
            <Field
              label="Est. ready (1 yr)"
              type="date"
              value={form.estimatedReadyDate}
              onChange={(e) => setField('estimatedReadyDate', e.target.value)}
            />
            <Field label="Bottled date" type="date" value={form.bottledDate} onChange={(e) => setField('bottledDate', e.target.value)} />
            <Field label="Yield (oz)" type="number" value={form.yieldOz} onChange={(e) => setField('yieldOz', e.target.value)} required />
            {isEdit && (
              <Field label="Remaining (oz)" type="number" value={form.remainingOz} onChange={(e) => setField('remainingOz', e.target.value)} />
            )}
          </Box>

          <Typography sx={{ fontWeight: 650, fontSize: 14, pt: 0.5 }}>Production details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
            <IngredientPick
              label="Bean / Spice"
              options={beanOptions}
              valueName={form.beanName}
              onPick={pickBean}
            />
            <IngredientPick
              label="Alcohol / Extract"
              options={alcoholOptions}
              valueName={form.alcoholName}
              onPick={pickAlcohol}
            />
            <Field
              label="Beans used (oz)"
              type="number"
              value={form.beansOz}
              onChange={(e) => setProductionField('beansOz', e.target.value)}
            />
            <Field
              label="Alcohol / Extract used (oz)"
              type="number"
              value={form.alcoholOz}
              onChange={(e) => setProductionField('alcoholOz', e.target.value)}
            />
            <Field
              label="Prod jar size (oz)"
              type="number"
              value={form.productionJarSizeOz}
              onChange={(e) => setProductionField('productionJarSizeOz', e.target.value)}
            />
            <Field
              label="Prod jars used"
              type="number"
              value={form.productionJarsUsed}
              onChange={(e) => setProductionField('productionJarsUsed', e.target.value)}
            />
            <Field
              label="Large labels used"
              type="number"
              value={form.largeLabelsUsed}
              onChange={(e) => setProductionField('largeLabelsUsed', e.target.value)}
            />
            <Field label="Labor hours" type="number" value={form.laborHours} onChange={(e) => setField('laborHours', e.target.value)} />
          </Box>

          <Field label="Notes" value={form.notes} onChange={(e) => setField('notes', e.target.value)} multiline minRows={2} />

          <Typography sx={{ fontWeight: 650, fontSize: 14, pt: 1 }}>Raw inputs</Typography>
          <Typography sx={{ color: tokens.inkMuted, fontSize: 12, mt: -0.5 }}>
            Add the inventory items used in this batch. Line cost uses each item’s current unit cost.
          </Typography>
          {form.inputs.map((row, index) => {
            const selected = options.find((o) => String(o.id) === String(row.ingredientId)) || null;
            const lineCost = cogsPreview.lines[index]?.lineCost || 0;
            return (
              <Stack key={`input-${index}`} direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  options={options}
                  value={selected}
                  onChange={(_, val) => {
                    setForm((prev) => ({
                      ...prev,
                      inputs: prev.inputs.map((r, i) => (i === index ? {
                        ...r,
                        ingredientId: val?.id ?? null,
                        name: val?.label || '',
                        measurement: val?.measurement || r.measurement || '',
                      } : r)),
                    }));
                  }}
                  getOptionLabel={(o) => o?.label || ''}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  sx={{ flex: 1.4 }}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label="Ingredient" size="small" />
                  )}
                />
                <TextField
                  label="Amt"
                  type="number"
                  size="small"
                  value={row.amount}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      inputs: prev.inputs.map((r, i) => (i === index ? { ...r, amount: e.target.value } : r)),
                    }));
                  }}
                  sx={{ width: 80 }}
                />
                <TextField
                  label="UoM"
                  size="small"
                  value={row.measurement}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      inputs: prev.inputs.map((r, i) => (i === index ? { ...r, measurement: e.target.value } : r)),
                    }));
                  }}
                  sx={{ width: 72 }}
                />
                <Typography sx={{ width: 72, textAlign: 'right', fontSize: 13, fontWeight: 650, color: tokens.ink }}>
                  {row.ingredientId && Number(row.amount) > 0 ? currencyFormatter(lineCost, 2) : '—'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setForm((prev) => ({
                    ...prev,
                    inputs: prev.inputs.length <= 1 ? [blankInput()] : prev.inputs.filter((_, i) => i !== index),
                  }))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })}
          <Button
            size="small"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setForm((prev) => ({ ...prev, inputs: [...prev.inputs, blankInput()] }))}
            sx={{ alignSelf: 'flex-start', color: tokens.accent, fontWeight: 650 }}
          >
            Add input
          </Button>

          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${tokens.border}`,
              backgroundColor: tokens.bg,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.inkMuted, fontWeight: 700 }}>
                Batch COGS
              </Typography>
              <Typography sx={{ fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650, fontSize: 20, color: tokens.ink }}>
                {currencyFormatter(cogsPreview.total, 2)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.inkMuted, fontWeight: 700 }}>
                Cost per oz
              </Typography>
              <Typography sx={{ fontFamily: "'Fraunces', 'Figtree', serif", fontWeight: 650, fontSize: 20, color: tokens.ink }}>
                {cogsPreview.costPerOz != null ? currencyFormatter(cogsPreview.costPerOz, 2) : '—'}
              </Typography>
            </Box>
            <Typography sx={{ gridColumn: '1 / -1', color: tokens.inkMuted, fontSize: 12 }}>
              {isEdit
                ? 'Save recalculates batch cost from current inventory unit costs. Reusable items use used stock first at $0.'
                : 'Pulled from inventory unit cost. Reusable items use used stock first at $0.'}
            </Typography>
          </Box>

          {error && (
            <Typography sx={{ color: tokens.danger, fontSize: 13 }}>{error}</Typography>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${tokens.border}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {submitting
            ? <CircularProgress size={18} sx={{ color: '#fff' }} />
            : (isEdit ? 'Save batch' : 'Create batch')}
        </Button>
      </Box>
    </Drawer>
  );
}

export default function Batches() {
  const [reload, setReload] = useState(false);
  const { data: envelope, load } = useFetch('api/batches', reload);
  const batches = envelope?.data || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [snack, setSnack] = useState({ open: false, color: '', message: '' });
  const { widths, onWidthChange } = useColumnWidths(DEFAULT_WIDTHS);

  const refresh = () => {
    setReload(true);
    setTimeout(() => setReload(false), 200);
  };

  const rows = useMemo(() => batches.map((b) => ({
    id: b.batchId,
    ...b,
  })), [batches]);

  const baseColumns = useMemo(() => ([
    { field: 'batchId', headerName: 'Batch ID', minWidth: 140 },
    {
      field: 'bornOn',
      headerName: 'Born on',
      minWidth: 110,
      type: 'date',
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => (value ? value.toLocaleDateString() : '—'),
    },
    {
      field: 'bottledDate',
      headerName: 'Bottled',
      minWidth: 110,
      type: 'date',
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => (value ? value.toLocaleDateString() : '—'),
    },
    {
      field: 'estimatedReadyDate',
      headerName: 'Est. ready',
      minWidth: 110,
      type: 'date',
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: ({ value }) => (value ? value.toLocaleDateString() : '—'),
    },
    { field: 'beanName', headerName: 'Bean', minWidth: 120, valueFormatter: ({ value }) => (value ? value : '—') },
    { field: 'beansOz', headerName: 'Beans oz', minWidth: 90, valueFormatter: ({ value }) => (value != null ? value : '—') },
    { field: 'alcoholName', headerName: 'Alcohol / Extract', minWidth: 140, valueFormatter: ({ value }) => (value ? value : '—') },
    { field: 'alcoholOz', headerName: 'Alcohol/Extract oz', minWidth: 130, valueFormatter: ({ value }) => (value != null ? value : '—') },
    { field: 'productionJarSizeOz', headerName: 'Jar oz', minWidth: 80, valueFormatter: ({ value }) => (value != null ? value : '—') },
    { field: 'remainingOz', headerName: 'Remaining', minWidth: 100 },
    {
      field: 'totalBatchCost',
      headerName: 'Batch cost',
      minWidth: 110,
      valueFormatter: ({ value }) => (value != null ? currencyFormatter(value, 2) : '—'),
    },
    {
      field: 'costPerOz',
      headerName: '$/oz',
      minWidth: 90,
      valueFormatter: ({ value }) => (value != null ? currencyFormatter(value, 2) : '—'),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      minWidth: 140,
      valueFormatter: ({ value }) => (value ? value : '—'),
    },
  ]), []);

  const columns = useMemo(
    () => withResizableColumns(baseColumns, widths, onWidthChange),
    [baseColumns, widths, onWidthChange],
  );

  function Toolbar() {
    return (
      <GridToolbarContainer>
        <Button
          className="recipe-bttn"
          startIcon={<AddOutlinedIcon sx={{ color: tokens.accent, fontSize: 18 }} />}
          onClick={() => { setEditing(null); setDrawerOpen(true); }}
          sx={{ textTransform: 'none', fontWeight: 650 }}
        >
          Add batch
        </Button>
        <Box sx={{ flex: 1 }} />
        <GridToolbarQuickFilter variant="outlined" size="small" sx={quickFilterSx} />
      </GridToolbarContainer>
    );
  }

  if (load && !envelope) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Production batches"
        subtitle="Click a row to edit usage, dates, and notes. Batch cost is calculated from inventory unit cost."
        actions={(
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => { setEditing(null); setDrawerOpen(true); }}
          >
            Add batch
          </Button>
        )}
      />

      <BatchDrawer
        open={drawerOpen}
        batch={editing}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        onSaved={(_, wasEdit) => {
          setSnack({
            open: true,
            color: 'success',
            message: wasEdit ? 'Batch updated' : 'Batch created',
          });
          refresh();
        }}
      />

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, pb: 4 }}>
        <Box
          sx={{
            border: `1px solid ${tokens.border}`,
            borderRadius: 2,
            backgroundColor: tokens.surface,
            overflow: 'hidden',
            height: { xs: 'calc(100vh - 220px)', md: 'calc(100vh - 200px)' },
            minHeight: 420,
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            slots={{ toolbar: Toolbar }}
            rowHeight={44}
            onRowClick={(params) => {
              setEditing(params.row);
              setDrawerOpen(true);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{
              ...dataGridSx,
              border: 'none',
              height: '100%',
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-columnHeader': { overflow: 'visible' },
              '& .MuiDataGrid-columnHeaderTitleContainer': { overflow: 'visible' },
            }}
          />
        </Box>
      </Box>

      {snack.open && (
        <SnackbarEx
          color={snack.color}
          message={snack.message}
          open={snack.open}
          close={() => setSnack({ open: false, color: '', message: '' })}
        />
      )}
    </Box>
  );
}
